// src/components/Sidebar.js
import React, { useState } from 'react';
import {
  ClipboardListIcon, UsersIcon, ShieldIcon, LogoutIcon,
  ChevronDownIcon, ChevronUpIcon,
} from './Icon';
import { can } from '../utils/permissions';

// Grouped navigation: every entry is a parent menu with a submenu. A group is
// shown only when the user may see at least one of its children, and each child
// is gated by its own permission. This keeps the sidebar tidy and permission
// driven — a viewer only ever sees the items their role unlocks.
const NAV = [
  { group: 'EOI', Icon: ClipboardListIcon, children: [
    { key: 'dashboard',   label: 'Dashboard',   perm: 'dashboard.view' },
    { key: 'submissions', label: 'Submissions', perm: 'submissions.view' },
  ] },
  { group: 'Customer Site Visit', Icon: UsersIcon, children: [
    { key: 'visitors',          label: 'Site Visit Records',     perm: 'visitors.view' },
    { key: 'visitor_dashboard', label: 'Site Visit Dashboard',   perm: 'visitor_dashboard.view' },
  ] },
  { group: 'Administration', Icon: ShieldIcon, children: [
    { key: 'projects', label: 'Projects',            perm: 'projects.view' },
    { key: 'users',    label: 'User Management',      perm: 'users.view' },
    { key: 'roles',    label: 'Roles & Permissions', perm: 'roles.view' },
  ] },
];

export default function Sidebar({ active, onChange, user, onLogout }) {
  const displayName = user?.name || user?.username || 'Admin';
  const initial = (displayName.trim()[0] || 'A').toUpperCase();

  // Keep only groups that have at least one child the user is allowed to open.
  const groups = NAV
    .map(g => ({ ...g, children: g.children.filter(c => can(user, c.perm)) }))
    .filter(g => g.children.length > 0);

  // A group starts expanded if it holds the active page; otherwise the first
  // visible group is opened so the menu is never fully collapsed on load.
  const [open, setOpen] = useState(() => {
    const init = {};
    groups.forEach(g => { init[g.group] = g.children.some(c => c.key === active); });
    if (groups.length && !Object.values(init).some(Boolean)) init[groups[0].group] = true;
    return init;
  });

  const toggle = (name) => setOpen(o => ({ ...o, [name]: !o[name] }));

  return (
    <aside className="sb">
      {/* Logo */}
      <div className="sb-logo-wrap">
        <img
          src={process.env.PUBLIC_URL + '/gpp-logo.png'}
          alt="Guru Punvaanii"
          className="sb-logo"
          onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
        />
        <div className="sb-logo-fallback">Guru Punvaanii</div>
        <div className="sb-tagline">Admin Portal</div>
      </div>

      {/* Nav */}
      <nav className="sb-nav">
        {groups.map(g => {
          const isOpen = !!open[g.group];
          const hasActive = g.children.some(c => c.key === active);
          return (
            <div className="sb-group" key={g.group}>
              <button
                type="button"
                className={`sb-group-header${hasActive ? ' has-active' : ''}`}
                onClick={() => toggle(g.group)}
                aria-expanded={isOpen}
              >
                <span className="sb-nav-icon"><g.Icon size={16} /></span>
                <span className="sb-group-label">{g.group}</span>
                <span className="sb-group-chevron">
                  {isOpen ? <ChevronUpIcon size={13} /> : <ChevronDownIcon size={13} />}
                </span>
              </button>
              {isOpen && (
                <div className="sb-submenu">
                  {g.children.map(c => (
                    <button
                      key={c.key}
                      type="button"
                      className={`sb-subitem${active === c.key ? ' is-active' : ''}`}
                      onClick={() => onChange(c.key)}
                    >
                      <span>{c.label}</span>
                      {active === c.key && <div className="sb-active-pill" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* User block */}
      <div className="sb-user">
        <div className="sb-avatar">{initial}</div>
        <div className="sb-user-info">
          <div className="sb-user-name">{displayName}</div>
          {user?.role && <div className="sb-user-role">{user.role}</div>}
        </div>
        <button type="button" className="sb-logout" onClick={onLogout} title="Logout" aria-label="Logout">
          <LogoutIcon size={16} />
        </button>
      </div>

      <div className="sb-footer">
        <div className="sb-footer-text">
          Fair Policy | Fair Property<br />
          <span className="sb-copyright">© 2019 Guru Punvaanii</span>
        </div>
      </div>
    </aside>
  );
}
